import { useEffect, useRef } from 'preact/hooks'
import { type HTMLAttributes } from 'preact/compat'

interface ICanvas extends HTMLAttributes<HTMLCanvasElement> {
	// One-shot render on mount.
	draw?: (ctx: CanvasRenderingContext2D) => void
	// Per-frame render driven by requestAnimationFrame. `t` is elapsed ms since start.
	loop?: (ctx: CanvasRenderingContext2D, t: number) => void
	// When true, the canvas backing size tracks its parent element's box.
	resizeToParent?: boolean
}

export default function Canvas({ draw, loop, resizeToParent, ...rest }: ICanvas) {
	const ref = useRef<HTMLCanvasElement>(null)

	useEffect(() => {
		const canvas = ref.current
		const ctx = canvas?.getContext('2d')
		if (!canvas || !ctx) return undefined

		let raf = 0
		let observer: ResizeObserver | undefined

		if (resizeToParent && canvas.parentElement) {
			const fit = () => {
				const { clientWidth, clientHeight } = canvas.parentElement as HTMLElement
				canvas.width = clientWidth
				canvas.height = clientHeight
			}
			fit()
			observer = new ResizeObserver(fit)
			observer.observe(canvas.parentElement)
		}

		if (typeof draw === 'function') draw(ctx)

		if (typeof loop === 'function') {
			const start = performance.now()
			const frame = (now: number) => {
				loop(ctx, now - start)
				raf = requestAnimationFrame(frame)
			}
			raf = requestAnimationFrame(frame)
		}

		return () => {
			if (raf) cancelAnimationFrame(raf)
			observer?.disconnect()
		}
	}, [draw, loop, resizeToParent])

	return <canvas ref={ref} {...rest} />
}

Canvas.defaultProps = {
	draw: undefined,
	loop: undefined,
	resizeToParent: false,
}
